import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import ScoresTable from "./scoresTable";
import SimulationInitialCharts from "../chart/simulationInitialCharts";
import PhysicalActivity from "./physicalActivity";
import HealthResults from "./heatlhResults";
import MorbidityResultsWalkersCyclists from "./morbidityResultsWalkersCyclists";
import MorbidityResultsYearsLived from "./morbidityResultsYearsLived";
import GenericTable from "./genericTable";
import GenericBars from "./genericBars";
import StyledTooltip from "../../utils/styledToolTip";
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import html2pdf from 'html2pdf.js';
import { formatDecimal } from "../../utils/numberFormat";

export default function FullResultsDialog({ open, onClose, projects }) {

  console.log("Projects to display simulation= ", projects)

  const [isPdfMode, setIsPdfMode] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const contentRef = useRef(null);

  const exportToPDF = () => {
    setIsPdfMode(true);
    setIsGeneratingPdf(true);

    setTimeout(() => {
      const element = contentRef.current;
      const opt = {
        margin: 0.15,
        filename: 'full-results.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 1.1,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          width: null,
          height: null
        },
        jsPDF: { 
          unit: 'in', 
          format: 'a4', 
          orientation: 'landscape',
          compress: true
        }
      };

      html2pdf()
        .set(opt)
        .from(element)
        .save()
        .then(() => {
          setIsPdfMode(false);
          setIsGeneratingPdf(false);
        })
        .catch((err) => {
          console.error('Erro ao gerar PDF:', err);
          setIsPdfMode(false);
          setIsGeneratingPdf(false);
        });
    }, 100);
  }

  useEffect(() => {
    if (open) {
      return;
    }
  }, [open]);

  const formatNumber = (value) => {
    if (typeof value === "number") {
      return formatDecimal(value, 0, 2);
    }
    return value ?? "—";
  }

  
  const formatWithSpaceThousands = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "-";
    return formatDecimal(value);
  }
  
  return (
    <Dialog
      className="c40v2"
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      sx={{ position: 'relative' }}
      PaperProps={{
        sx: {      
          position: "fixed",
          bottom: 0,
          width: 'min(96vw, 1400px)',
          maxWidth: 'unset',
          height: "auto",
          display: "flex",
          flexDirection: "column",
          minHeight: "90vh",
          px: 2,
          pt: 1,
          pb: 0
        },
      }}>

      {isGeneratingPdf && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 9999,
            bgcolor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
          }}
        >
          <CircularProgress />
          <Typography sx={{ mt: 2, fontWeight: 'bold', color: '#d3d2d2' }}>
            Generating the PDF...
          </Typography>
        </Box>
      )}

      <Box
        ref={contentRef}
        sx={{
          position: 'relative',
          overflow: isGeneratingPdf ? 'hidden' : 'auto',
          pointerEvents: isGeneratingPdf ? 'none' : 'auto',
        }}
      >
        <Typography variant="h5"
          component="h5"
          sx={{
            textAlign: "left",
            fontWeight: "bold",
            textTransform: "uppercase",
            mb: 1,
            mt: 2,
            ml: 2, // 16px à direita
          }}>
          Impact on Indicators
        </Typography>

      <DialogContent sx={{ overflow: 'visible' }}>
        <Box display="grid" gap={isPdfMode ? 0.75 : 3}>
          <Box
            display="grid"
            gap={3}
            sx={{
              width: "100%",
              position: "relative",
              zIndex: "auto",
              minWidth: "1000px"
            }}
          >
            <SimulationInitialCharts projects={projects} forPdf={isPdfMode} />
          </Box>

          {/* General score */}
          <Box sx={{ mt:2, mb:4, ...(isPdfMode && { mt: -5, mb: 0.25 })}}>
            <Box display="flex" alignItems="center" gap={0} sx={{ pr: isPdfMode ? 1.5 : 2.5 }}>
              <Typography variant="h6">General score</Typography>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1, cursor: "help" }}
              >
              <StyledTooltip title="General Score calculated as the product of Mobility and Livability Scores" arrow><span className="icon"></span></StyledTooltip>
              </Typography>
              <Box flexGrow={1} />
              {projects.map((project) => (
                <Box key={project.actionSimulationId} sx={{ width: isPdfMode ? 100 : 120, flex: isPdfMode ? '0 0 100px' : '0 0 120px', textAlign: 'center', overflow: 'hidden' }}>
                <Typography variant={isPdfMode ? "body2" : "subtitle1"} color="text.secondary" sx={{ fontWeight: 600, px: isPdfMode ? 0.5 : 1, lineHeight: isPdfMode ? 1.2 : 1.4 }}>
                  {project.projectName}
                </Typography>
                </Box>
              ))}
              <Box sx={{ width: isPdfMode ? 24 : 40, maxWidth: isPdfMode ? 24 : 40 }}></Box>
            </Box>

            {/* Barra azul com total */}
            <Box
              sx={{
                mt: isPdfMode ? 0.8 : 1.5,
                bgcolor: "#23bced",
                color: "primary.contrastText",
                borderRadius: 0.5,
                display: "flex",
                alignItems: "center",
                px: isPdfMode ? 1.25 : 2,
                py: isPdfMode ? 0.75 : 1.25,
                pr: isPdfMode ? 0.75 : 1
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>
                Total score (Mobility × Livability)
              </Typography>
              <Box flexGrow={1} />              
              {projects.map((project) => (              
                <Box key={project.actionSimulationId} sx={{ width: isPdfMode ? 100 : 120, flex: isPdfMode ? '0 0 100px' : '0 0 120px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    {formatWithSpaceThousands(project.projectInfo.postProjectLivabilityCombined * project.projectInfo.postProjectWalkabilityAndCyclabilityCombined)}
                  </Typography>
                </Box>    
              ))}
               <Box sx={{ width: isPdfMode ? 28 : 50, maxWidth: isPdfMode ? 28 : 50 }}></Box>
            </Box>
          </Box>
                    
          {/* Mobility scores */}
          <ScoresTable projects={projects} sectionTitle="Mobility scores" forPdf={isPdfMode} />

          {/*<Box sx={{ pageBreakBefore: isPdfMode ? 'always' : 'auto' }}> */}
            <ScoresTable projects={projects} sectionTitle="Liveability scores" forPdf={isPdfMode} />
          {/*</Box> */}
          
        </Box>

        <Box display="grid" gap={3} 
          sx={{ mt: 6, ...(isPdfMode && { mt: 0, pageBreakBefore: 'always' }) }}>

          <Typography variant="h5"
            component="h5"
            sx={{
              textAlign: "left",
              fontWeight: "bold",
              textTransform: "uppercase",
              mt: 4,
            }}>
            Benefits
          </Typography>

          <PhysicalActivity projects={projects} />

          <HealthResults projects={projects} forPdf={isPdfMode}/>

          <Typography variant="subtitle1"
            sx={{textTransform: "uppercase", fontSize: "1rem", mt: 5, ...(isPdfMode && { mt:0, mb:0, pageBreakBefore: 'always' })}}>
              Morbidity Reduction
          </Typography>

          <MorbidityResultsWalkersCyclists projects={projects} forPdf={isPdfMode}/>

          <MorbidityResultsYearsLived projects={projects} forPdf={isPdfMode}/>

          <Box sx={{ ...(isPdfMode && { pageBreakBefore: 'always' }) }}>
            <GenericTable 
              projects={projects} 
              title="Economic Value of Health Benefits" 
              forPdf={isPdfMode} 
              tooltip={
                <Box>
                  <Typography>
                    The <b>value of statistical life (VSL)</b> is the additional monetary cost that individuals would be willing to pay to reduce their risk of death.
                  </Typography>
                  <Typography sx={{ mt: 1 }}>
                    The <b>value of a life year (VOLY)</b> is the additional monetary value that individuals would be willing to pay to extend their life by one year.
                  </Typography>
                </Box>
              }
            />
            <GenericTable 
              projects={projects} 
              title="Emissions" 
              forPdf={isPdfMode}
              tooltip="Emissions saved due to mode shift from polluting transport modes (car, taxi, public transport) to walking and cycling."
            />
          </Box>

          <GenericBars projects={projects} title="Emissions" forPdf={isPdfMode} />
            
        </Box>

      </DialogContent>
      </Box>

      <DialogActions sx={{ position: "sticky", bottom: 0, bgcolor: "background.paper", zIndex: 10 }}>
         <Stack direction="row" spacing={2} alignItems="center" sx={{ pb: 2, pt: 3 }}>
          
          <Button variant="outlined" onClick={() => exportToPDF()} endIcon={<ArrowCircleDownIcon />}
            sx={{ borderColor: "#00aaff", color: "#00aaff", py: 1, px: 2 }}>
            Download PDF
          </Button>

          <Button
            onClick={onClose}
            variant="contained"
            color="primary"
            sx={{
              mr: 2,
              py: 1,
              px: 2,
              fontWeight: "bold",
              backgroundColor: "#23bced",
              "&:hover": { backgroundColor: "#23bced" },
            }}>
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
